import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Link,
  Divider,
} from '@mui/material';

const GeneCard = ({ gene, constraint }) => {
  const externalLinks = {
    ensembl: `https://www.ensembl.org/Homo_sapiens/Gene/Summary?g=${gene.ensembl_id}`,
    ncbi: gene.entrez_id
      ? `https://www.ncbi.nlm.nih.gov/gene/${gene.entrez_id}`
      : null,
    gnomad: `https://gnomad.broadinstitute.org/gene/${gene.ensembl_id}?dataset=gnomad_r4`,
    genebass: `https://app.genebass.org/gene/${gene.ensembl_id}?burdenSet=pLoF&phewasOpts=1&resultLayout=full`,
    decipher: `https://www.deciphergenomics.org/gene/${gene.symbol}/phenotypes/phenotypes-pathogenic`,
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {gene.symbol}
            </Typography>
            {gene.name && (
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {gene.name}
              </Typography>
            )}
            {gene.location && (
              <Chip label={`Location: ${gene.location}`} size="small" sx={{ mt: 1 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
            <Link href={externalLinks.ensembl} target="_blank" rel="noopener" sx={{ fontSize: '0.875rem' }}>
              Ensembl
            </Link>
            {externalLinks.ncbi && (
              <Link href={externalLinks.ncbi} target="_blank" rel="noopener" sx={{ fontSize: '0.875rem' }}>
                NCBI
              </Link>
            )}
            <Link href={externalLinks.gnomad} target="_blank" rel="noopener" sx={{ fontSize: '0.875rem' }}>
              gnomAD
            </Link>
            <Link href={externalLinks.genebass} target="_blank" rel="noopener" sx={{ fontSize: '0.875rem' }}>
              GeneBass
            </Link>
            <Link href={externalLinks.decipher} target="_blank" rel="noopener" sx={{ fontSize: '0.875rem' }}>
              DECIPHER
            </Link>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Typography variant="body2">
            <strong>Ensembl ID:</strong> {gene.ensembl_id}
          </Typography>
          {gene.entrez_id && (
            <Typography variant="body2">
              <strong>Entrez ID:</strong> {gene.entrez_id}
            </Typography>
          )}
          {gene.hgnc_id && (
            <Typography variant="body2">
              <strong>HGNC ID:</strong> {gene.hgnc_id}
            </Typography>
          )}
        </Box>

        {/* Constraint Metrics */}
        {constraint && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              gnomAD Constraint Metrics (v4.0)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {constraint.lof_pli !== null && constraint.lof_pli !== undefined && (
                <Chip 
                  label={`pLI: ${constraint.lof_pli.toFixed(3)}`} 
                  size="small" 
                  color={constraint.lof_pli > 0.9 ? 'error' : constraint.lof_pli > 0.5 ? 'warning' : 'default'}
                />
              )}
              {(constraint.lof_oe_upper !== null && constraint.lof_oe_upper !== undefined) || 
               (constraint.lof_oe_ci && constraint.lof_oe_ci[1] !== null && constraint.lof_oe_ci[1] !== undefined) ? (
                <Chip 
                  label={`LOEUF: ${(constraint.lof_oe_upper ?? constraint.lof_oe_ci[1]).toFixed(3)}`} 
                  size="small"
                  color={(constraint.lof_oe_upper ?? constraint.lof_oe_ci[1]) < 0.35 ? 'error' : (constraint.lof_oe_upper ?? constraint.lof_oe_ci[1]) < 0.7 ? 'warning' : 'default'}
                />
              ) : null}
              {constraint.lof_oe_ci && constraint.lof_oe_ci[0] != null && constraint.lof_oe_ci[1] != null && (
                <Typography variant="caption" color="text.secondary">
                  (95% CI: {constraint.lof_oe_ci[0].toFixed(3)}-{constraint.lof_oe_ci[1].toFixed(3)})
                </Typography>
              )}
              {constraint.mis_oe !== null && constraint.mis_oe !== undefined && (
                <Chip 
                  label={`Missense OE: ${constraint.mis_oe.toFixed(3)}`} 
                  size="small"
                />
              )}
              {constraint.lof_obs !== null && constraint.lof_exp !== null && (
                <Typography variant="caption" color="text.secondary">
                  LoF: {constraint.lof_obs} obs / {constraint.lof_exp.toFixed(1)} exp
                </Typography>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GeneCard;

